import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:25',message:'profileOperations.load entry',data:{userId},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        const { data, error } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', userId)
          ?.single();
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:30',message:'profileOperations.load result',data:{hasError:!!error,errorCode:error?.code,hasData:!!data},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        // Silently handle errors - profile is optional
        if (!error && data) setUserProfile(data);
      } catch (error) {
        // Silently ignore all errors
      } finally {
        setProfileLoading(false);
      }
    },
    
    clear() {
      setUserProfile(null);
      setProfileLoading(false);
    }
  };

  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        profileOperations?.load(session?.user?.id);
      } else {
        profileOperations?.clear();
      }
    }
  };

  // ⭐ FIX: FORCE redirect to extension BEFORE routing happens ⭐
  const handleExtensionRedirect = (session) => {
    try {
      const url = new URL(window.location.href);
      const extId = url.searchParams.get('extension_id');

      // Not extension login
      if (!extId) return;

      // Must be logged in
      if (!session?.access_token) return;

      console.log("Redirecting token to extension:", extId);

      // CRITICAL: Do this BEFORE React navigates anywhere
      window.location.replace(
        `chrome-extension://${extId}/auth-bridge.html?token=${session.access_token}`
      );

    } catch (err) {
      console.error("Extension redirect error:", err);
    }
  };

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      // ⭐ Redirect FIRST
      handleExtensionRedirect(session);

      authStateHandlers?.onChange(null, session);
    });

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      (event, session) => {
        // ⭐ Redirect FIRST
        handleExtensionRedirect(session);

        authStateHandlers?.onChange(event, session);
      }
    );

    return () => subscription?.unsubscribe();
  }, []);

  const signUp = async (email, password, options = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: { data: options?.userData || {} }
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      if (error) return { data: null, error };
      if (data?.user) {
        setUser(data.user);
        await profileOperations.load(data.user.id);
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      setUser(null);
      setUserProfile(null);
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const resetPassword = async (email) => {
    try {
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port;
      const origin = port ? `${protocol}//${hostname}:${port}` : window.location.origin;
      const redirectUrl = `${origin}/email-confirmation?type=recovery`;

      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: redirectUrl
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) throw new Error('Missing session');

      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;